import React, { useMemo } from 'lib/react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'lib/redux';
import {
  EVCheckbox,
  EVGrid,
  EVTypography,
  EVRadioButtonUncheckedIcon,
  EVRadioButtonCheckedIcon,
} from 'lib/components';
import { updateSelectedProduct, updateSelectedOtherProducts } from 'features/order/Order.actions';
import { getProductFromId } from 'features/order/Order.utils';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { gainsightPxCustomEvent } from 'utils/gainsightPx.utils';
import { GAINSIGHT_PX_EVENT_PRODUCT_SELECTED } from 'constants/gainsightPxCustomEvents.constants';
import ProductStyles from './ProductStyles';
import { THREE_HOUR_DELIVERY_PRODUCT_ID, WALLS_PRODUCTS, DATA_PRODUCTS } from './ProductList.constants';
import ProductListExpansionPanel from './ProductListExpansionPanel.component';
import ProductListTemplate from './ProductList.template';

function PrimaryProductTemplate(props) {
  const {
    name, products, icon, selectedProductKey, selectedDeliveryProductKey, selectComponentType,
  } = props;
  const {
    [selectedProductKey]: selectedProduct, [selectedDeliveryProductKey]: selectedDeliveryProduct,
    selectedPrimaryProduct,
    selectedWallsProduct,
    selectedOtherProducts,
  } = useSelector((state) => state.order);

  const dispatch = useDispatch();
  const classes = ProductStyles();

  const radioIconProps = {
    icon: <EVRadioButtonUncheckedIcon />,
    checkedIcon: <EVRadioButtonCheckedIcon />,
  };
  const displayCategory = useMemo(() => products.length > 0, [products]);

  // Check if Property Data is selected (mutual exclusivity)
  const isPropertyDataSelected = useMemo(() => Boolean(
    selectedPrimaryProduct?.ProductID === PRODUCT_IDS.PropertyData,
  ), [selectedPrimaryProduct]);

  // For Data Products category, we use selectedPrimaryProduct when it's Property Data
  const isDataProductsCategory = name === DATA_PRODUCTS;
  // #genai: extract nested ternaries into independent helper variables (Sonar S3358); evaluation order/behavior unchanged
  const isSelectedPrimaryProductPropertyData = Boolean(
    selectedPrimaryProduct?.ProductID === PRODUCT_IDS.PropertyData,
  );
  const propertyDataProduct = isSelectedPrimaryProductPropertyData ? selectedPrimaryProduct : null;
  const propertyDataDeliveryProduct = isSelectedPrimaryProductPropertyData
    ? selectedPrimaryProduct.selectedDeliveryProduct
    : null;

  const actualSelectedProduct = isDataProductsCategory
    ? propertyDataProduct
    : selectedProduct;

  const actualSelectedDeliveryProduct = isDataProductsCategory
    ? propertyDataDeliveryProduct
    : selectedDeliveryProduct;

  const setSelectedProduct = (updatedState) => dispatch(updateSelectedProduct(updatedState));
  const setSelectedDeliveryProduct = (value) => dispatch(updateSelectedProduct({ [selectedDeliveryProductKey]: value }));

  const handlePrimaryProductSelect = (product) => {
    const productId = product.ProductID;
    const deliveryProductId = product.WebDeliveryProducts[0].ProductID;

    // Handle Property Data (Data Products category) - use selectedPrimaryProduct state
    if (isDataProductsCategory && productId === PRODUCT_IDS.PropertyData) {
      if (selectedPrimaryProduct?.ProductID === productId) {
        // If Property Data is already selected, deselect it
        gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_SELECTED, { productId: null, productName: null });
        dispatch(updateSelectedProduct({
          selectedPrimaryProduct: null,
          selectedPrimaryDeliveryProduct: null,
        }));
      } else {
        // Clear all other products and select Property Data
        gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_SELECTED, { productId, productName: product.Description });
        dispatch(updateSelectedProduct({
          selectedPrimaryProduct: {
            ...product,
            selectedDeliveryProduct: deliveryProductId,
          },
          selectedPrimaryDeliveryProduct: deliveryProductId,
          selectedWallsProduct: null,
          selectedWallsDeliveryProduct: null,
        }));
        dispatch(updateSelectedOtherProducts({}));
      }
      return;
    }

    if (selectedProduct?.ProductID === product.ProductID) {
      // If clicking the same product, deselect it
      gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_SELECTED, { productId: null, productName: null });
      setSelectedProduct({
        [selectedProductKey]: null,
        [selectedDeliveryProductKey]: null,
      });
    } else if (name === WALLS_PRODUCTS && selectedPrimaryProduct) {
      // For walls products when a primary product is already selected
      setSelectedProduct({
        [selectedProductKey]: product,
        [selectedDeliveryProductKey]: product.WebDeliveryProducts[0].ProductID,
        selectedPrimaryDeliveryProduct: product.WebDeliveryProducts[0].ProductID,
      });
      gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_SELECTED, { productId, productName: product.Description });
    } else {
      // For radio button behavior: select the new product (this automatically deselects others)
      gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_SELECTED, { productId, productName: product.Description });
      setSelectedProduct({
        [selectedProductKey]: product,
        [selectedDeliveryProductKey]: product.WebDeliveryProducts[0].ProductID,
      });
    }
  };

  const handleDeliveryProductSelect = (deliveryProductId, productId) => {
    if (isDataProductsCategory && productId === PRODUCT_IDS.PropertyData) {
      // Handle delivery selection for Property Data using selectedPrimaryProduct
      if (selectedPrimaryProduct?.ProductID === productId) {
        dispatch(updateSelectedProduct({
          selectedPrimaryProduct: {
            ...selectedPrimaryProduct,
            selectedDeliveryProduct: deliveryProductId,
          },
          selectedPrimaryDeliveryProduct: deliveryProductId,
        }));
      }
    } else {
      // Handle delivery selection for other products
      setSelectedDeliveryProduct(deliveryProductId);
    }
  };

  const anyProductsSelected = (productId) => {
    if (isDataProductsCategory) {
      return Boolean(selectedPrimaryProduct?.ProductID === productId);
    }
    return selectedProduct?.ProductID === productId;
  };

  const threeHourDeliverySelected = (productId) => {
    if (isDataProductsCategory) {
      return selectedPrimaryProduct?.ProductID === productId
        && selectedPrimaryProduct.selectedDeliveryProduct === THREE_HOUR_DELIVERY_PRODUCT_ID;
    }
    // For regular products, check if this specific product is selected AND has 3-hour delivery
    return selectedProduct?.ProductID === productId
      && selectedDeliveryProduct === THREE_HOUR_DELIVERY_PRODUCT_ID;
  };

  const getSelectedProductsMessage = (productDescription, deliveryType) => `${productDescription} with ${deliveryType} Delivery`;

  const getSelectedProducts = () => {
    if (isDataProductsCategory) {
      // Handle Data Products display using selectedPrimaryProduct
      if (!selectedPrimaryProduct || selectedPrimaryProduct.ProductID !== PRODUCT_IDS.PropertyData) return null;

      const product = products.find((p) => p.ProductID === selectedPrimaryProduct.ProductID);
      if (!product) return null;

      const deliveryProduct = product.WebDeliveryProducts.find(
        (dp) => dp.ProductID === selectedPrimaryProduct.selectedDeliveryProduct,
      );

      // Show "Instant" for Property Data instead of "Regular"
      const deliveryName = deliveryProduct?.Name === 'Regular' ? 'Instant' : deliveryProduct?.Name;

      return (
        <EVGrid item xs={12} sm={12} md={12} lg={12} className={classes.subHeader}>
          <EVTypography>
            {getSelectedProductsMessage(product.Description, deliveryName)}
          </EVTypography>
        </EVGrid>
      );
    }

    // Handle regular products display
    return actualSelectedProduct
      && products.some((product) => product.ProductID === actualSelectedProduct.ProductID) && (
      <EVGrid item xs={12} sm={12} md={12} lg={12} className={classes.subHeader}>
        <EVTypography>
          {getSelectedProductsMessage(
            actualSelectedProduct.Description,
            // For Property Data, show "Instant" instead of "Regular"
            actualSelectedProduct.ProductID === PRODUCT_IDS.PropertyData
              ? 'Instant'
              : getProductFromId(actualSelectedProduct.WebDeliveryProducts, actualSelectedDeliveryProduct)?.Name,
          )}
        </EVTypography>
      </EVGrid>
    );
  };

  const getProductSelectComponent = (productId, isIncompatible, handleChange) => {
    const isChecked = isDataProductsCategory
      ? Boolean(selectedPrimaryProduct?.ProductID === productId)
      : actualSelectedProduct?.ProductID === productId;

    // For Data Products, disable Walls and Other products when Property Data is selected
    const isDisabledByPropertyData = !isDataProductsCategory && isPropertyDataSelected
      && (name === WALLS_PRODUCTS || name.includes('Other'));

    return (
      <EVCheckbox
        id={`products-${productId}`}
        checked={isChecked}
        value={productId}
        name="product-selected"
        inputProps={{ 'aria-label': 'Product Selection' }}
        disabled={isIncompatible || isDisabledByPropertyData}
        onChange={handleChange}
        {...(selectComponentType === 'radio' && radioIconProps)}
      />
    );
  };

  const productListComponent = () => {
    const getSelectedDeliveryProductForTemplate = (productId) => {
      if (isDataProductsCategory) {
        return selectedPrimaryProduct?.ProductID === productId ? selectedPrimaryProduct.selectedDeliveryProduct : null;
      }
      return actualSelectedDeliveryProduct;
    };

    // Determine if this category should be disabled
    const hasOtherProductsSelected = !isDataProductsCategory && (
      selectedPrimaryProduct
      || selectedWallsProduct
      || Object.keys(selectedOtherProducts || {}).length > 0
    );

    const isDisabled = isDataProductsCategory
      ? hasOtherProductsSelected
      : (name === WALLS_PRODUCTS && isPropertyDataSelected);

    return (
      <ProductListTemplate
        products={products}
        getSelectedDeliveryProduct={getSelectedDeliveryProductForTemplate}
        anyProductsSelected={anyProductsSelected}
        getProductSelectComponent={getProductSelectComponent}
        threeHourDeliverySelected={threeHourDeliverySelected}
        handleProductSelect={handlePrimaryProductSelect}
        handleDeliveryProductSelect={handleDeliveryProductSelect}
        isDisabled={isDisabled}
        categoryName={name}
      />
    );
  };

  return displayCategory && (
    <ProductListExpansionPanel
      {...{ name, icon }}
      getSelectedProductsSubHeader={getSelectedProducts}
      productList={productListComponent}
    />
  );
}

PrimaryProductTemplate.propTypes = {
  name: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  icon: PropTypes.element.isRequired,
  selectedProductKey: PropTypes.string.isRequired,
  selectedDeliveryProductKey: PropTypes.string.isRequired,
  selectComponentType: PropTypes.string.isRequired,
};
export default PrimaryProductTemplate;
